import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Edit, useForm } from '@refinedev/antd';
import { Form, Input, InputNumber, Select, Switch, Row, Col, Card, message } from 'antd';
import { IResourceComponentsProps, useNavigation } from '@refinedev/core';
import { axiosInstance } from '@/services/api';
import { PlayerData } from '@/types/index';
import { BaseStatsFormSection } from './BaseStatsFormSection';
import { syncPlayerTypeAndRole } from './syncPlayerTypeAndRole';

const BASE_STAT_FIELDS = [
  'role',
  'phySpeed',
  'phyStamina',
  'phyPower',
  'phyReflex',
  'phyAcrobatics',
  'sklFootwork',
  'sklTiming',
  'sklRange',
  'sklBowling',
  'sklWk',
  'accBat',
  'accBowl',
  'accField',
  'cmp',
];

export const PlayerDataEdit: React.FC<IResourceComponentsProps> = () => {
  const { formProps, saveButtonProps, form, queryResult } = useForm<PlayerData>();
  const { show } = useNavigation();
  const [includeBaseStats, setIncludeBaseStats] = useState(false);
  const [saving, setSaving] = useState(false);
  const hadExistingBaseStats = useRef(false);

  const record = queryResult?.data?.data;

  // Populate base stats fields from existing data
  useEffect(() => {
    if (record?.baseStats) {
      hadExistingBaseStats.current = true;
      setIncludeBaseStats(true);
      const statsValues: Record<string, unknown> = {};
      for (const field of BASE_STAT_FIELDS) {
        let value: unknown = record.baseStats[field as keyof typeof record.baseStats];
        if (value !== null && value !== undefined) {
          // accField comes as string from DB decimal column — convert to number
          if (field === 'accField' && typeof value === 'string') value = Number(value);
          statsValues[`baseStats_${field}`] = value;
        }
      }
      form?.setFieldsValue(statsValues);
    }
  }, [record, form]);

  const handleValuesChange = useCallback(
    (changed: Record<string, unknown>) => {
      if (!form || !includeBaseStats) return;
      syncPlayerTypeAndRole(form, changed);
    },
    [form, includeBaseStats]
  );

  const handleFinish = async (values: Record<string, unknown>) => {
    if (!record) return;
    setSaving(true);
    try {
      const masterData: Record<string, unknown> = {};
      const baseStatsData: Record<string, unknown> = {};

      for (const [key, value] of Object.entries(values)) {
        if (key.startsWith('baseStats_')) {
          if (value != null && value !== '') baseStatsData[key.replace('baseStats_', '')] = value;
        } else {
          masterData[key] = value;
        }
      }

      // accField is decimal — ensure it's sent as a number, not a string
      if (baseStatsData.accField != null) {
        baseStatsData.accField = Number(baseStatsData.accField);
      }

      const playerIndex = record.playerIndex;

      await axiosInstance.put(`/admin/player-data/${playerIndex}`, masterData);

      if (includeBaseStats) {
        if (hadExistingBaseStats.current) {
          await axiosInstance.put(`/admin/player-data/${playerIndex}/base-stats`, baseStatsData);
        } else {
          baseStatsData.playerIndex = playerIndex;
          await axiosInstance.post(`/admin/player-data/${playerIndex}/base-stats`, baseStatsData);
        }
      }

      void message.success('Player updated successfully');
      show('player-data', playerIndex);
    } catch (err: unknown) {
      const error = err as { response?: { data?: { message?: string } } };
      void message.error(error?.response?.data?.message || 'Failed to update player');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Edit
      saveButtonProps={{
        ...saveButtonProps,
        loading: saving,
        onClick: () => form?.submit(),
      }}
    >
      <Form
        {...formProps}
        layout="vertical"
        onFinish={handleFinish}
        onValuesChange={handleValuesChange}
      >
        <Card title="Player Identity" style={{ marginBottom: 16 }}>
          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Player Index" name="playerIndex">
                <InputNumber disabled style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="First Name" name="firstName" rules={[{ required: true }]}>
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Second Name" name="secondName">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Display Name" name="displayName" rules={[{ required: true }]}>
                <Input maxLength={100} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Nationality" name="nationality">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item label="Team Name" name="teamName">
                <Input maxLength={50} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item label="Team Index" name="teamIndex" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Jersey Number" name="jerseyNumber" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Face Asset ID" name="faceAssetId" rules={[{ required: true }]}>
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Sort Order" name="sortOrder">
                <InputNumber min={0} style={{ width: '100%' }} />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item label="Player Type" name="defaultPlayerType" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'Batsman', value: 0 },
                    { label: 'Bowler', value: 1 },
                    { label: 'All-Rounder', value: 2 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Bowler Type" name="defaultBowlerType" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'Non-Bowler', value: -1 },
                    { label: 'Fast', value: 0 },
                    { label: 'Off Break', value: 1 },
                    { label: 'Leg Break', value: 2 },
                    { label: 'Medium', value: 3 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Batting Hand" name="battingHand" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'Right', value: 0 },
                    { label: 'Left', value: 1 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Bowling Hand" name="bowlingHand" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'Right', value: 0 },
                    { label: 'Left', value: 1 },
                  ]}
                />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={6}>
              <Form.Item label="Bowling Action" name="bowlingAction" rules={[{ required: true }]}>
                <Select
                  options={[
                    { label: 'Normal', value: 0 },
                    { label: 'Side-arm', value: 1 },
                    { label: 'Round-arm', value: 2 },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Role" name="role">
                <Select
                  allowClear
                  options={[
                    { label: 'Captain', value: 'Captain' },
                    { label: 'Vice-Captain', value: 'Vice-Captain' },
                    { label: 'Player', value: 'Player' },
                  ]}
                />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Wicket Keeper" name="isWicketKeeper" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Winner Alliance" name="isWinnerAlliance" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
            <Col xs={24} sm={6}>
              <Form.Item label="Active" name="isActive" valuePropName="checked">
                <Switch />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Min Build Version"
                name="minBuildVersion"
                tooltip="Hide this player from builds older than this. Leave as 1 to show it to every build. Player masters are gated by build number rather than config version, because cards players already own look their master up by index."
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="1" />
              </Form.Item>
            </Col>
            <Col xs={24} sm={8}>
              <Form.Item
                label="Max Build Version"
                name="maxBuildVersion"
                tooltip="Hide this player from builds newer than this. Leave empty for no upper limit."
              >
                <InputNumber min={1} style={{ width: '100%' }} placeholder="No limit" />
              </Form.Item>
            </Col>
          </Row>

          <Row gutter={16}>
            <Col xs={24} sm={8}>
              <Form.Item label="Body Asset ID" name="bodyAssetId">
                <Input maxLength={100} />
              </Form.Item>
            </Col>
          </Row>
        </Card>

        <Card
          title="Base Stats"
          style={{ marginBottom: 16 }}
          extra={
            <Switch
              checked={includeBaseStats}
              onChange={setIncludeBaseStats}
              checkedChildren="On"
              unCheckedChildren="Off"
            />
          }
        >
          {includeBaseStats ? (
            <BaseStatsFormSection />
          ) : (
            <div style={{ color: '#999', textAlign: 'center', padding: 16 }}>
              Toggle on to add base stats for this player
            </div>
          )}
        </Card>
      </Form>
    </Edit>
  );
};
